// Penghitung untuk baris "Total" di dasar tabel. Murni (tanpa React) supaya mudah diuji.
// Aturan: total dihitung dari SEMUA baris hasil filter (bukan hanya yang tampak di layar).
import { formatIDR, formatDate, methodLabel } from './format';

const TIDAK_DIHITUNG = ['Void', 'Draf'];   // sama dengan STATUS_TIDAK_DIHITUNG di backend
const angka = (v) => Number(v) || 0;

export const periodeLabel = (start, end) => {
  if (start && end) return `${formatDate(start)} – ${formatDate(end)}`;
  if (start) return `sejak ${formatDate(start)}`;
  if (end) return `sampai ${formatDate(end)}`;
  return 'semua periode';
};

// ---------- Kas & Pengeluaran ----------
export const hitungKas = (rows) => {
  const h = { jumlah: 0, masuk: 0, keluar: 0, transfer: 0, nTransfer: 0, tidakDihitung: 0 };
  (rows || []).forEach((r) => {
    if (TIDAK_DIHITUNG.includes(r.status)) { h.tidakDihitung += 1; return; }
    if (r.direction === 'in') { h.masuk += angka(r.amount); h.jumlah += 1; }
    else if (r.direction === 'out') { h.keluar += angka(r.amount); h.jumlah += 1; }
    else { h.transfer += angka(r.amount); h.nTransfer += 1; h.jumlah += 1; }
  });
  return h;
};

export const susunTotalKas = (rows, filters = {}, sumberLabel = {}) => {
  const h = hitungKas(rows);
  const dir = filters.direction || '';
  const bagian = [dir === 'in' ? 'Total Uang Masuk' : dir === 'out' ? 'Total Uang Keluar' : dir === 'transfer' ? 'Total Transfer Internal' : 'Total hasil filter'];
  bagian.push(periodeLabel(filters.start, filters.end));
  if (filters.source) bagian.push(sumberLabel[filters.source] || filters.source);
  if (filters.perlu_cek) bagian.push('perlu dicek');
  if ((filters.q || '').trim()) bagian.push(`cari “${filters.q.trim()}”`);
  let items;
  if (dir === 'in') items = [{ label: 'Transaksi', value: String(h.jumlah), id: 'jumlah' }, { label: 'Total Masuk', value: formatIDR(h.masuk), tone: 'in', id: 'masuk' }];
  else if (dir === 'out') items = [{ label: 'Transaksi', value: String(h.jumlah), id: 'jumlah' }, { label: 'Total Keluar', value: formatIDR(h.keluar), tone: 'out', id: 'keluar' }];
  else if (dir === 'transfer') items = [{ label: 'Transaksi', value: String(h.jumlah), id: 'jumlah' }, { label: 'Total Dipindah', value: formatIDR(h.transfer), id: 'transfer' }];
  else {
    const selisih = h.masuk - h.keluar;
    items = [
      { label: 'Transaksi', value: String(h.jumlah), id: 'jumlah' },
      { label: 'Total Masuk', value: formatIDR(h.masuk), tone: 'in', id: 'masuk' },
      { label: 'Total Keluar', value: formatIDR(h.keluar), tone: 'out', id: 'keluar' },
      { label: 'Selisih (Masuk − Keluar)', value: (selisih < 0 ? '-' : '') + formatIDR(Math.abs(selisih)), tone: selisih < 0 ? 'out' : 'in', id: 'selisih' },
    ];
  }
  const cat = [];
  if (h.tidakDihitung > 0) cat.push(`${h.tidakDihitung} baris void/draf tidak dihitung`);
  if (!dir && h.nTransfer > 0) cat.push(`${h.nTransfer} transfer antar kas tidak masuk Masuk/Keluar`);
  return { judul: bagian.join(' · '), items, catatan: cat.join(' · '), h };
};

// ---------- Order Sewa ----------
export const susunTotalOrder = (list, filterBarang = '') => {
  const semua = list || [];
  const batal = semua.filter((t) => t.status === 'cancelled');
  const aktif = semua.filter((t) => t.status !== 'cancelled');
  if (filterBarang === 'cancelled') {
    const hangus = batal.reduce((s, t) => s + angka(t.cancel_forfeited), 0);
    const kembali = batal.reduce((s, t) => s + angka(t.cancel_refunded), 0);
    return {
      judul: 'Total order dibatalkan',
      items: [{ label: 'Order', value: String(batal.length), id: 'jumlah' }, { label: 'DP hangus', value: formatIDR(hangus), tone: 'in', id: 'hangus' }, { label: 'DP dikembalikan', value: formatIDR(kembali), tone: 'out', id: 'kembali' }],
      catatan: '',
    };
  }
  const nilai = aktif.reduce((s, t) => s + angka(t.total), 0);
  const sisa = aktif.reduce((s, t) => s + angka(t.remaining), 0);
  return {
    judul: 'Total hasil filter',
    items: [
      { label: 'Order', value: String(aktif.length), id: 'jumlah' },
      { label: 'Nilai order', value: formatIDR(nilai), id: 'nilai' },
      { label: 'Sudah dibayar', value: formatIDR(nilai - sisa), tone: 'in', id: 'dibayar' },
      { label: 'Sisa tagihan', value: formatIDR(sisa), tone: sisa > 0 ? 'out' : undefined, id: 'sisa' },
    ],
    catatan: batal.length > 0 ? `${batal.length} order dibatalkan tidak dihitung` : '',
  };
};

// ---------- Laporan Pembayaran ----------
export const susunTotalBayar = (data) => {
  if (!data) return null;
  const orders = data.orders || [];
  const sisa = orders.reduce((s, o) => s + angka(o.remaining), 0);
  const s = data.summary || {};
  return {
    judul: `Total hasil filter · ${periodeLabel((data.rentang || [])[0], (data.rentang || [])[1])}`,
    items: [
      { label: 'Pembayaran', value: String(s.total_payments || 0), id: 'jumlah' },
      { label: 'Uang masuk', value: formatIDR(s.total_uang_masuk), tone: 'in', id: 'masuk' },
      { label: 'DP', value: formatIDR(s.total_dp), id: 'dp' },
      { label: 'Pelunasan', value: formatIDR(s.total_pelunasan), id: 'pelunasan' },
      { label: 'Sisa tagihan order', value: formatIDR(sisa), tone: sisa > 0 ? 'out' : undefined, id: 'sisa' },
    ],
    catatan: '',
  };
};

// ---------- Laporan: tabel pembayaran per metode ----------
export const susunTotalRekap = (rows, metode = '') => {
  const pakai = (rows || []).filter((r) => !metode || r.method === metode);
  const total = pakai.reduce((s, r) => s + angka(r.amount), 0);
  return {
    judul: metode ? `Total pembayaran · ${methodLabel(metode)}` : 'Total pembayaran',
    items: [{ label: 'Pembayaran', value: String(pakai.length), id: 'jumlah' }, { label: 'Total', value: formatIDR(total), tone: 'in', id: 'total' }],
    catatan: '',
  };
};
